/**
 * PHI MASK — deterministic fake-data substitution for demos and training videos.
 *
 * WHY THIS EXISTS
 *   The original demo mode (GENERAL_DEMO_MODE_ON, 2023) masked patient surnames
 *   with "*****" via six helpers in src/mixin.js. It rotted for a structural
 *   reason: masking was OPT-IN AT EACH CALL SITE. Only 110 of 547 .vue files even
 *   import the mixin, `formatEmail` was never called once, and `formatName` only
 *   ever masked the surname — so "*****, Bibi (57)" leaked a real first name, a
 *   real age, and (one pill over) a real email address.
 *
 *   This module inverts that. It is applied at the DATA BOUNDARY by
 *   src/plugins/phiMaskInterceptor.js, so a component must actively bypass the
 *   net to leak rather than actively opt in to be safe. New features are covered
 *   by default.
 *
 * DETERMINISM IS THE WHOLE POINT
 *   Every fake value is a pure function of the REAL value. No randomness, no
 *   stored map, no persistence. Consequences that matter on camera:
 *     - A patient shows the SAME fake name on every page, every session, forever.
 *       (Random-per-render would make the video look broken.)
 *     - Real surname -> fake surname is 1:1, so families stay coherent in the
 *       Family Members / Family Recall tabs without needing GuarNum (which the
 *       frontend does not have).
 *     - A row carrying both `LName`/`FName` AND a composite `Patient` ("Doe, Bibi")
 *       masks to values that AGREE, because both go through the same functions.
 *   Hashing the real value is one-way: it reveals nothing and needs no storage.
 *
 * THIS FILE IS PURE
 *   No Vue, no Vuex, no localStorage, no imports. On/off state lives in
 *   src/utils/phiMaskState.js. That split is what lets
 *   cypress/node-tests/phi-mask-*.test.js load and execute THIS FILE DIRECTLY
 *   rather than mirroring it — the tests bind to shipped code.
 *
 * SCOPE (decided 2026-08-26)
 *   Masked:     patient names, phones, emails, street address / city / ZIP,
 *               date of birth (year+month kept so displayed AGE stays true),
 *               an APPOINTMENT's note (2026-10-24: free text on an object that
 *               carries an AptNum is replaced by a fixed harmless line).
 *   NOT masked: age, state, staff/provider names, insurance subscriber IDs,
 *               carrier names, and anything that is not patient data. Other
 *               human-typed free text (a commlog's Note, a task's text) is
 *               still NOT masked.
 */

/* ---------------------------------------------------------------------------
 * MASK_STYLE — the one knob worth flipping after you see it on video.
 *
 *   'literal'   phones -> (XXX) XXX-XXXX, emails -> xxxx@email.com
 *               Unmistakably masked. Every patient's contact info looks alike.
 *
 *   'realistic' phones -> (609) 555-0142, emails -> m.alvarez@examplemail.com
 *               The 555-0100..555-0199 range is formally reserved for fiction
 *               (NANP), so it reads as a real number on camera but can never
 *               dial a person. Looks far better in the Campaigns audience table
 *               and the Conversations list, where 'literal' turns every row into
 *               the same string.
 *
 * Names, addresses and DOB are always realistic — the whole reason for this
 * rewrite was that "*****" looks bad in a training video.
 * ------------------------------------------------------------------------- */
export const MASK_STYLE = 'literal';

/* -- Deterministic hash (FNV-1a, 32-bit) ------------------------------------
 * Chosen for being tiny, dependency-free, and stable across Node and browsers.
 * It is NOT cryptographic — it does not need to be. It is a one-way bucket
 * selector, and the buckets are public name banks. */
export function hash32(str) {
   let h = 0x811c9dc5;
   const s = String(str == null ? '' : str);
   for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
   }
   return h >>> 0;
}

/** Normalize a real value before hashing so casing/spacing can't split a family. */
function norm(v) {
   return String(v == null ? '' : v).trim().toLowerCase().replace(/\s+/g, ' ');
}

function pick(bank, seed) {
   return bank[hash32(seed) % bank.length];
}

/* -- Name banks -------------------------------------------------------------
 * Deliberately broad and ordinary. These need to read as a real dental
 * practice's patient roster on camera, which means a plausible mix rather than
 * a themed or obviously-generated list. */
const FIRST_NAMES = [
   'Maria', 'James', 'Aisha', 'Daniel', 'Priya', 'Marcus', 'Elena', 'Thomas',
   'Nia', 'Andrew', 'Sofia', 'Gregory', 'Leila', 'Patrick', 'Naomi', 'Victor',
   'Camila', 'Nathan', 'Rosa', 'Dominic', 'Hannah', 'Simon', 'Yara', 'Curtis',
   'Beatriz', 'Malcolm', 'Ingrid', 'Terrence', 'Adaeze', 'Lorenzo', 'Freya',
   'Desmond', 'Anika', 'Rafael', 'Colette', 'Bernard', 'Sana', 'Owen', 'Marta',
   'Julian', 'Nadia', 'Clifford', 'Imani', 'Stefan', 'Rowena', 'Hugo', 'Talia',
   'Everett', 'Mina', 'Roland', 'Delphine', 'Oscar', 'Kenji', 'Bianca', 'Ivan',
   'Solange', 'Warren', 'Fatima', 'Emmett', 'Larisa', 'Gideon', 'Noor', 'Alden',
   'Paloma', 'Sterling', 'Hana', 'Bruno', 'Odette', 'Xavier', 'Junia', 'Cormac',
   'Selina', 'Aurelio', 'Wren', 'Basil', 'Marisol', 'Theodore', 'Lucia', 'Amos',
   'Petra', 'Ellis', 'Rhoda', 'Cyrus', 'Annika', 'Foster', 'Isla', 'Barrett',
   'Sunita', 'Quentin', 'Verena', 'Dashiell', 'Orla', 'Lionel', 'Zara', 'Ambrose',
   'Cecile', 'Hollis', 'Ravi', 'Junie', 'Ephraim', 'Tamsin',
];

const LAST_NAMES = [
   'Alvarez', 'Whitfield', 'Okonkwo', 'Brennan', 'Castellanos', 'Nakamura',
   'Delacroix', 'Ferraro', 'Lindqvist', 'Ashworth', 'Mbeki', 'Vargas',
   'Kowalski', 'Thornbury', 'Rahimi', 'Sandoval', 'Ellingham', 'Duarte',
   'Petrov', 'Mackenzie', 'Osei', 'Beaumont', 'Villanueva', 'Halloran',
   'Sorensen', 'Achebe', 'Fontaine', 'Grimaldi', 'Radcliffe', 'Bhatt',
   'Novotny', 'Cardoza', 'Whitlock', 'Ivanova', 'Marchetti', 'Adeyemi',
   'Blackwood', 'Salazar', 'Lindgren', 'Farouk', 'Prescott', 'Moreau',
   'Tanaka', 'Kavanagh', 'Estrada', 'Wolcott', 'Bergstrom', 'Nwachukwu',
   'Caldwell', 'Rios', 'Hargrove', 'Mikhailov', 'Santoro', 'Broughton',
   'Adekunle', 'Vasquez', 'Emberly', 'Kaufman', 'Quintero', 'Larsson',
   'Winterbourne', 'Haddad', 'Colville', 'Mendoza', 'Ferguson', 'Oyelaran',
   'Strand', 'Pemberton', 'Aguilar', 'Rosetti', 'Halvorsen', 'Chowdhury',
   'Lockhart', 'Barros', 'Underwood', 'Nakashima', 'Cervantes', 'Ravenscroft',
   'Diallo', 'Kingsley', 'Montalvo', 'Ekstrom', 'Whitaker', 'Reyes',
   'Ainsworth', 'Bouchard', 'Sotelo', 'Danforth', 'Ibrahim', 'Calloway',
   'Vespucci', 'Hollenbeck', 'Okafor', 'Trevino', 'Marlowe', 'Fitzgerald',
   'Anand', 'Sowande', 'Rutherford', 'Peralta',
];

const STREET_NAMES = [
   'Maple', 'Chestnut', 'Windermere', 'Larkspur', 'Bramblewood', 'Harrowgate',
   'Kingfisher', 'Sycamore', 'Belmont', 'Foxglove', 'Ravenna', 'Thistledown',
   'Corliss', 'Fernbank', 'Ashgrove', 'Pemberton', 'Willowmere', 'Halstead',
   'Juniper', 'Marchmont', 'Clearbrook', 'Ellingworth', 'Sandpiper', 'Rosemont',
];
const STREET_TYPES = ['St', 'Ave', 'Rd', 'Ln', 'Dr', 'Ct', 'Way', 'Ter'];
const CITIES = [
   'Fairmont', 'Ridgeway', 'Elmhurst', 'Northbrook', 'Stanton', 'Westhaven',
   'Clayburn', 'Springvale', 'Oakmere', 'Brookfield', 'Highmoor', 'Larkfield',
];
/* Area codes below are real NANP codes, but every number they carry is in the
 * 555-0100..555-0199 fiction range, so nothing here is dialable. */
const AREA_CODES = ['201', '609', '732', '856', '908', '973', '212', '718'];

/* -- Field-level fakers --------------------------------------------------- */

/**
 * Real first name -> fake first name. Seeded ONLY on the first name, so the
 * mapping needs no context: a row that carries just `fName` and a row that
 * carries the full record produce the SAME fake. Every real "Bibi" becomes the
 * same fake first name, which is exactly what makes the demo coherent.
 */
export function fakeFirst(realFirst) {
   if (!realFirst) return realFirst;
   return pick(FIRST_NAMES, 'F|' + norm(realFirst));
}

/**
 * Real surname -> fake surname, seeded only on the surname. This is what gives
 * FAMILY CONSISTENCY for free: everyone who really shares a surname lands on the
 * same fake one, so the Family Members tab and guarantor rows stay coherent.
 */
export function fakeLast(realLast) {
   if (!realLast) return realLast;
   return pick(LAST_NAMES, 'L|' + norm(realLast));
}

/** Preserve the shape of the real phone; only the digits change. */
export function maskPhone(phone) {
   if (!phone) return phone;
   const s = String(phone);
   if (MASK_STYLE === 'literal') {
      return s.replace(/[0-9]/g, 'X');
   }
   const digits = s.replace(/\D/g, '');
   if (digits.length < 10) return s.replace(/[0-9]/g, 'X');
   const h = hash32('P|' + digits);
   const area = AREA_CODES[h % AREA_CODES.length];
   const line = String(h % 100).padStart(2, '0'); // 555-0100 .. 555-0199
   return '(' + area + ') 555-01' + line;
}

/** Derived from the FAKE name so the address book reads consistently. */
export function fakeEmail(fFirst, fLast) {
   if (MASK_STYLE === 'literal') return 'xxxx@email.com';
   const a = String(fFirst || 'patient').charAt(0).toLowerCase();
   const b = String(fLast || 'demo').toLowerCase().replace(/[^a-z]/g, '');
   return a + '.' + b + '@examplemail.com';
}

/**
 * Insurance member / subscriber ID — SHAPE-PRESERVING.
 *
 * A HIPAA Safe Harbor identifier ("health plan beneficiary number", #9), and it
 * is rendered in a monospace pill right under the patient's name in the
 * Insurance Verification drawer, so it is very legible on camera.
 *
 * Every letter becomes another letter, every digit another digit, and
 * separators are kept — so "W180258834" masks to something like "K492817360"
 * rather than a row of X's. Carriers use wildly different ID formats, and a
 * plausible one keeps the drawer looking real.
 *
 * I and O are excluded from the letter bank so a masked ID never reads
 * ambiguously as 1 or 0 when someone squints at a video frame.
 */
const ID_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ';

export function maskMemberId(id) {
   if (!id) return id;
   const s = String(id);
   let out = '';
   for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch >= '0' && ch <= '9') {
         out += String(hash32('MD|' + s + '|' + i) % 10);
      } else if (/[A-Za-z]/.test(ch)) {
         const c = ID_LETTERS[hash32('ML|' + s + '|' + i) % ID_LETTERS.length];
         out += (ch === ch.toLowerCase()) ? c.toLowerCase() : c;
      } else {
         out += ch; // keep dashes, spaces, slashes
      }
   }
   return out;
}

/** Seeded on the REAL address so a household keeps one street. */
export function fakeAddress(realAddress) {
   if (!realAddress) return realAddress;
   const h = hash32('A|' + norm(realAddress));
   const num = 100 + (h % 900);
   const street = STREET_NAMES[(h >>> 4) % STREET_NAMES.length];
   const type = STREET_TYPES[(h >>> 9) % STREET_TYPES.length];
   return num + ' ' + street + ' ' + type;
}

export function fakeCity(realCity) {
   if (!realCity) return realCity;
   return pick(CITIES, 'C|' + norm(realCity));
}

export function fakeZip(realZip) {
   if (!realZip) return realZip;
   return String(10000 + (hash32('Z|' + norm(realZip)) % 89999));
}

/**
 * Shift the day-of-month only, keeping YEAR AND MONTH.
 *
 * Why not a full shift: the displayed AGE is deliberately left real (age under
 * 90 is not a Safe Harbor identifier on its own, and "(57)" reads far better on
 * camera than "(**)"). A DOB that computes to a different age than the age
 * column next to it looks like a bug on video. Keeping year+month makes the two
 * agree — except for patients whose birthday falls inside the CURRENT month,
 * where the displayed age can differ by 1. Rare and cosmetic; documented rather
 * than solved.
 */
export function shiftDob(dob) {
   if (!dob) return dob;
   const s = String(dob);
   const day = 1 + (hash32('D|' + s) % 28); // 1..28, valid in every month
   const dd = String(day).padStart(2, '0');
   // ISO-ish: 1968-03-14 or 1968-03-14T00:00:00
   const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
   if (iso) return iso[1] + '-' + iso[2] + '-' + dd + iso[4];
   // US: 03/14/1968
   const us = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
   if (us) return us[1] + '/' + dd + '/' + us[3];
   return s;
}

/* -- The appointment note -----------------------------------------------------
 * Open Dental's "Appointment Note" (`appt.Note`) is FREE TEXT the office types — "afraid of needles",
 * "her mother pays, call her" — and Schedule View draws it on the block, the checklist card, the phone
 * card and the Table row (2026-10-24). Free text cannot be faked field by field the way a name or a
 * phone can, so an APPOINTMENT's note is replaced wholesale by a fixed, harmless line.
 *
 * Which line is a pure function of the real note (same note -> same fake, on every screen and in every
 * session), so a day on camera does not shimmer. Only appointment-shaped objects (see
 * maskPatientObject) are touched: commlogs, tasks and every other `Note` key are out of scope. */
export const APPT_NOTE_FAKES = [
   'Prefers morning appointments',
   'Bring updated insurance card',
   'Asked about whitening options',
   'Called ahead - running 10 min late',
   'Confirm the day before',
];

export function fakeApptNote(realNote) {
   if (!realNote) return realNote;
   return pick(APPT_NOTE_FAKES, 'N|' + norm(realNote));
}

/** An appointment carries an AptNum. Open Dental's 0 means "no appointment", so it is not one. */
function hasAptNum(obj) {
   const v = obj.AptNum;
   if (v === undefined || v === null) return false;
   const s = String(v).trim();
   return s !== '' && s !== '0';
}

/* -- Composite names ---------------------------------------------------------
 * The API returns names in three shapes on different endpoints:
 *   "Doe, Bibi"  (SQL CONCAT(LName,', ',FName) — the AdminTable `Patient` column)
 *   "Bibi Doe"   (plain join)
 *   "Doe"        (single token)
 * All three route through fakeFirst/fakeLast, so a composite field and the
 * component LName/FName fields on the SAME row always agree. */
export function maskCompositeName(value) {
   if (!value || typeof value !== 'string') return value;
   const s = value.trim();
   if (!s) return value;

   const comma = s.indexOf(',');
   if (comma > -1) {
      const last = s.slice(0, comma).trim();
      const rest = s.slice(comma + 1).trim();
      const parts = rest.split(/\s+/).filter(Boolean);
      const first = parts.shift() || '';
      // Keep a trailing middle initial (e.g. "Doe, Bibi F") but drop nothing else.
      const tail = parts.filter(p => /^[A-Za-z]\.?$/.test(p)).join(' ');
      const out = fakeLast(last) + ', ' + fakeFirst(first) + (tail ? ' ' + tail : '');
      return out.trim();
   }

   const parts = s.split(/\s+/).filter(Boolean);
   if (parts.length === 1) return fakeLast(parts[0]);
   const first = parts[0];
   const last = parts[parts.length - 1];
   return fakeFirst(first) + ' ' + fakeLast(last);
}

/* -- Field dictionaries ------------------------------------------------------
 * UNAMBIGUOUS keys are OpenDental patient columns. They are masked on sight,
 * because nothing else in the app uses these names.
 *
 * AMBIGUOUS keys are masked ONLY inside an object that already looks like a
 * patient record. This is the single most important safety property in the file:
 * `Email` appears 584 times across src/ and `Phone` 94 times, and MOST of those
 * are not patient data — staff logins, SendGrid verified senders, campaign
 * from-addresses, the site's own Twilio number, A2P wizard numbers, Email
 * Reports recipients. Masking those blanket-style would break login and sending.
 */
const LAST_NAME_KEYS = ['LName', 'lName', 'LastName'];
const FIRST_NAME_KEYS = ['FName', 'fName', 'FirstName', 'Preferred'];
/* `SubscriberName` is here because the insurance subscriber is a real person —
 * usually the patient or a parent/spouse. It renders at CreateFollowup.vue:862. */
const NAME_KEYS_COMPOSITE = ['PatientName', 'Patient', 'Guarantor', 'patientName', 'patName', 'SubscriberName'];
const PHONE_KEYS_UNAMBIGUOUS = ['HmPhone', 'WkPhone', 'WirelessPhone', 'HomePhone', 'WorkPhone', 'patientPhone', 'wirelessPhone'];
const AMBIGUOUS_KEYS = ['Email', 'email', 'Phone', 'phone', 'Name', 'Address', 'Address2', 'City', 'Zip', 'address', 'city', 'zip'];

/* DOB is UNAMBIGUOUS — masked on sight, not gated on looksLikePatient().
 * The gate exists to protect Email/Phone, where over-masking breaks login and
 * sending. Nothing in the app ever sends TO a birth date, so over-masking one
 * is harmless, while under-masking one leaks PHI. Note `SubscriberBirthDate`,
 * which renders at CreateFollowup.vue:863 on an object that may carry no
 * PatNum at all. */
const DOB_KEYS = ['Birthdate', 'BirthDate', 'birthdate', 'DOB', 'dob', 'SubscriberBirthDate', 'subscriberBirthDate'];

/* Insurance member / subscriber IDs. Verified repo-wide: these key names are
 * only ever insurance identifiers — nothing else in the app uses them — so they
 * are safe to mask on sight. Note the odd `SubScriberID` casing, which is how
 * the claim payload actually spells it (CreateFollowup.vue:861). */
const MEMBER_ID_KEYS = ['memberId', 'MemberID', 'MemberId', 'memberID',
   'SubScriberID', 'SubscriberID', 'subscriberId', 'SubscriberId'];

/** Keys that alone are enough to call an object a patient record. */
const PATIENT_MARKERS = ['PatNum', 'patNum', 'PatientName', 'patientName'];

/**
 * Does this object represent a patient? Drives whether AMBIGUOUS keys get
 * masked. Deliberately conservative — a false NEGATIVE leaks one field on one
 * screen; a false POSITIVE can mask a staff email and break a login or a send.
 */
export function looksLikePatient(obj) {
   if (!obj || typeof obj !== 'object') return false;
   for (let i = 0; i < PATIENT_MARKERS.length; i++) {
      const v = obj[PATIENT_MARKERS[i]];
      if (v !== undefined && v !== null && v !== '') return true;
   }
   // LName + FName together is an OpenDental person row.
   if ((obj.LName != null || obj.lName != null) && (obj.FName != null || obj.fName != null)) return true;
   // A `Patient` / `Guarantor` column holding "Last, First".
   if (typeof obj.Patient === 'string' && obj.Patient.indexOf(',') > -1) return true;
   if (typeof obj.Guarantor === 'string' && obj.Guarantor.indexOf(',') > -1) return true;
   return false;
}

/**
 * Mask one object in place. The real values are preserved on a NON-ENUMERABLE
 * `__phi` property, so they are invisible to JSON.stringify, Object.keys, v-for,
 * b-table renderers and CSV builders — but still recoverable via unmask() if a
 * path ever genuinely needs truth.
 */
export function maskPatientObject(obj) {
   if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
   if (Object.prototype.hasOwnProperty.call(obj, '__phi')) return obj; // already masked

   const isPatient = looksLikePatient(obj);
   const real = {};
   let touched = false;

   const setMasked = (key, masked) => {
      if (obj[key] === masked) return;
      real[key] = obj[key];
      obj[key] = masked;
      touched = true;
   };

   for (let i = 0; i < LAST_NAME_KEYS.length; i++) {
      const k = LAST_NAME_KEYS[i];
      if (typeof obj[k] === 'string' && obj[k]) setMasked(k, fakeLast(obj[k]));
   }
   for (let i = 0; i < FIRST_NAME_KEYS.length; i++) {
      const k = FIRST_NAME_KEYS[i];
      if (typeof obj[k] === 'string' && obj[k]) setMasked(k, fakeFirst(obj[k]));
   }
   if (typeof obj.MiddleI === 'string' && obj.MiddleI) setMasked('MiddleI', 'R');

   for (let i = 0; i < NAME_KEYS_COMPOSITE.length; i++) {
      const k = NAME_KEYS_COMPOSITE[i];
      if (typeof obj[k] === 'string' && obj[k]) setMasked(k, maskCompositeName(obj[k]));
   }
   for (let i = 0; i < PHONE_KEYS_UNAMBIGUOUS.length; i++) {
      const k = PHONE_KEYS_UNAMBIGUOUS[i];
      if (obj[k]) setMasked(k, maskPhone(obj[k]));
   }
   for (let i = 0; i < DOB_KEYS.length; i++) {
      const k = DOB_KEYS[i];
      if (obj[k]) setMasked(k, shiftDob(obj[k]));
   }
   for (let i = 0; i < MEMBER_ID_KEYS.length; i++) {
      const k = MEMBER_ID_KEYS[i];
      if (obj[k]) setMasked(k, maskMemberId(obj[k]));
   }

   // An appointment's free-text note. Gated on AptNum, NOT on looksLikePatient(): `Note` is on
   // commlogs, procedures and a dozen other rows, and only the appointment's is in scope.
   if (typeof obj.Note === 'string' && obj.Note.trim() && hasAptNum(obj)) {
      setMasked('Note', fakeApptNote(obj.Note));
   }

   if (isPatient) {
      // Derive the email from whatever fake name this row ended up with, so the
      // address matches the name shown beside it.
      const fFirst = obj.FName || obj.fName || obj.FirstName || '';
      const fLast = obj.LName || obj.lName || obj.LastName || '';
      for (let i = 0; i < AMBIGUOUS_KEYS.length; i++) {
         const k = AMBIGUOUS_KEYS[i];
         const v = obj[k];
         if (!v || typeof v !== 'string') continue;
         if (/email/i.test(k)) setMasked(k, fakeEmail(fFirst, fLast));
         else if (/phone/i.test(k)) setMasked(k, maskPhone(v));
         else if (/^address/i.test(k)) setMasked(k, fakeAddress(v));
         else if (/^city$/i.test(k)) setMasked(k, fakeCity(v));
         else if (/^zip$/i.test(k)) setMasked(k, fakeZip(v));
         else if (k === 'Name') setMasked(k, maskCompositeName(v));
      }
   }

   if (touched) {
      Object.defineProperty(obj, '__phi', {
         value: real,
         enumerable: false,   // invisible to JSON.stringify / Object.keys / exports
         writable: true,
         configurable: true,
      });
   }
   return obj;
}

/** Recover a real field value that masking replaced. */
export function unmask(obj, key) {
   if (!obj || typeof obj !== 'object') return undefined;
   if (!obj.__phi) return obj[key];
   return Object.prototype.hasOwnProperty.call(obj.__phi, key) ? obj.__phi[key] : obj[key];
}

/* -- The walker --------------------------------------------------------------
 * Depth- and node-capped. A runaway API payload must never freeze the UI: the
 * caps degrade masking on absurd payloads rather than hanging the tab. */
const MAX_DEPTH = 12;
const MAX_NODES = 200000;

export function maskDeep(payload) {
   let budget = MAX_NODES;

   const walk = (node, depth) => {
      if (budget-- <= 0 || depth > MAX_DEPTH) return node;
      if (!node || typeof node !== 'object') return node;

      if (Array.isArray(node)) {
         for (let i = 0; i < node.length; i++) walk(node[i], depth + 1);
         return node;
      }
      maskPatientObject(node);
      for (const k in node) {
         if (!Object.prototype.hasOwnProperty.call(node, k)) continue;
         const v = node[k];
         if (v && typeof v === 'object') walk(v, depth + 1);
      }
      return node;
   };

   return walk(payload, 0);
}
